import React, { useState, useEffect } from "react";
import { useTheme } from "../contexts/ThemeContext";
import {
  Camera,
  MapPin,
  Calendar,
  Phone,
  Mail,
  AlertTriangle,
  Upload,
  Search,
  CheckCircle,
  ArrowLeft,
  Zap,
  Users,
  Clock,
} from "lucide-react";
import { httpsCallable } from "firebase/functions";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { functions, storage } from "../firebase";
import { useNavigate } from "react-router-dom";
import MapPicker from "../components/MapPicker";

const FoundPetPage = ({ user, onComplete = null, isModal = false }) => {
  const { themeClasses } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Photo, 2: Details, 3: Matches, 4: Success
  const [matches, setMatches] = useState([]);
  const [submissionResult, setSubmissionResult] = useState(null);

  const [formData, setFormData] = useState({
    // Photo and AI Detection
    photoFile: null,
    photoURL: "",
    breedDetected: "",
    breedConfidence: 0,

    // Location
    foundLocation: "",
    foundAddress: "",
    locationData: null,

    // Basic Details
    foundDate: new Date().toISOString().split("T")[0],
    foundTime: new Date().toTimeString().slice(0, 5),
    petSize: "",
    petColor: "",
    petGender: "",
    distinctiveFeatures: "",
    behaviorNotes: "",
    currentLocation: "", // Where pet is now

    // Contact Info
    finderName: user?.displayName || "",
    finderPhone: "",
    finderEmail: user?.email || "",
    preferredContact: "email", // email, phone, app

    // Additional Info
    description: "",
    hasCollar: "",
    collarDescription: "",
    petCondition: "good", // good, injured, sick
    urgentCare: false,
  });

  // Auto-detect breed when photo is uploaded
  const detectBreedFromPhoto = async (photoFile) => {
    try {
      setLoading(true);

      // Upload photo first
      const photoRef = ref(
        storage,
        `found-pets/${Date.now()}_${photoFile.name}`
      );
      await uploadBytes(photoRef, photoFile);
      const photoURL = await getDownloadURL(photoRef);

      // Call breed detection API
      const detectBreed = httpsCallable(functions, "detectDogBreed");
      const result = await detectBreed({ imageUrl: photoURL });

      if (result.data.success) {
        setFormData((prev) => ({
          ...prev,
          photoURL,
          breedDetected: result.data.breed,
          breedConfidence: result.data.confidence,
        }));
      }
    } catch (error) {
      console.error("Error detecting breed:", error);
    } finally {
      setLoading(false);
    }
  };

  // Search for matching lost pets
  const searchForMatches = async () => {
    try {
      setLoading(true);

      const searchLostDogs = httpsCallable(functions, "searchLostDogs");
      const result = await searchLostDogs({
        breed: formData.breedDetected,
        location: formData.locationData,
        foundDate: formData.foundDate,
        size: formData.petSize,
        color: formData.petColor,
        radius: 25, // 25 mile radius
      });

      if (result.data.success) {
        setMatches(result.data.matches || []);
        setStep(3);
      }
    } catch (error) {
      console.error("Error searching for matches:", error);
    } finally {
      setLoading(false);
    }
  };

  // Submit found pet report
  const submitFoundPetReport = async () => {
    try {
      setLoading(true);

      // Create the found pet report
      const reportFoundPet = httpsCallable(functions, "reportFoundPet");
      const result = await reportFoundPet({
        ...formData,
        reporterId: user?.uid,
        potentialMatches: matches.map((m) => m.id),
      });

      if (result.data.success) {
        setSubmissionResult(result.data);
        setStep(4);
        // Call onComplete for modal mode
        if (onComplete) {
          onComplete();
        }
      }
    } catch (error) {
      console.error("Error submitting found pet report:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, photoFile: file }));
      await detectBreedFromPhoto(file);
      setStep(2);
    }
  };

  const handleLocationSelect = (locationData) => {
    setFormData((prev) => ({
      ...prev,
      locationData,
      foundLocation: locationData.address,
      foundAddress: locationData.address,
    }));
  };

  const renderPhotoStep = () => (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <Camera
          className={`w-16 h-16 ${themeClasses.text.accent} mx-auto mb-4`}
        />
        <h2 className={`text-3xl font-bold ${themeClasses.text.primary} mb-2`}>
          Found a Pet?
        </h2>
        <p className={`text-lg ${themeClasses.text.secondary}`}>
          Take a clear photo of the pet's face to help us identify potential
          matches
        </p>
      </div>

      <div
        className={`${themeClasses.bg.card} rounded-xl border ${themeClasses.border.primary} p-8`}
      >
        <div className="text-center">
          <label
            className={`inline-flex items-center justify-center w-full p-8 border-2 border-dashed ${themeClasses.border.primary} rounded-lg cursor-pointer hover:bg-opacity-50 ${themeClasses.bg.secondary} transition-colors`}
          >
            <div>
              <Upload
                className={`w-12 h-12 ${themeClasses.text.secondary} mx-auto mb-4`}
              />
              <span
                className={`text-lg font-medium ${themeClasses.text.primary}`}
              >
                Click to take or upload photo
              </span>
              <p className={`text-sm ${themeClasses.text.secondary} mt-2`}>
                JPG, PNG up to 10MB
              </p>
            </div>
            <input
              type="file"
              accept="image/*"
              capture="camera"
              onChange={handlePhotoUpload}
              className="hidden"
            />
          </label>
        </div>

        {loading && (
          <div className="mt-6 text-center">
            <div className="inline-flex items-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
              <span className={themeClasses.text.secondary}>
                Analyzing photo and detecting breed...
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div
      className={`${isModal ? "" : "min-h-screen"} ${themeClasses.bg.primary} ${
        isModal ? "" : "py-8"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            {!isModal && (
              <button
                onClick={() => navigate("/dashboard")}
                className={`mr-4 p-2 rounded-lg ${themeClasses.bg.card} border ${themeClasses.border.primary} hover:bg-opacity-80 transition-colors`}
              >
                <ArrowLeft className={`w-5 h-5 ${themeClasses.text.primary}`} />
              </button>
            )}
            <h1 className={`text-4xl font-bold ${themeClasses.text.primary}`}>
              Report Found Pet
            </h1>
          </div>

          {/* Progress Indicator */}
          <div className="flex items-center justify-center space-x-4 mb-8">
            {[1, 2, 3, 4].map((stepNum) => (
              <div key={stepNum} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    step >= stepNum
                      ? "bg-blue-600 text-white"
                      : `${themeClasses.bg.secondary} ${themeClasses.text.secondary}`
                  }`}
                >
                  {stepNum}
                </div>
                {stepNum < 4 && (
                  <div
                    className={`w-12 h-1 mx-2 ${
                      step > stepNum ? "bg-blue-600" : themeClasses.bg.secondary
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step Content - Only showing Photo step for now */}
        {step === 1 && renderPhotoStep()}

        {/* TODO: Add other steps */}
        {step > 1 && (
          <div className="text-center">
            <p className={`text-lg ${themeClasses.text.secondary}`}>
              Step {step} coming soon! This demonstrates the API-first approach.
            </p>
            <button
              onClick={() => setStep(1)}
              className={`mt-4 px-6 py-3 ${themeClasses.bg.button} text-white rounded-lg`}
            >
              Back to Photo Step
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FoundPetPage;
